import { inject, Injectable } from '@angular/core'
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular'
import {
    AccountInfo,
    AuthenticationResult,
    BrowserAuthError,
    InteractionRequiredAuthError,
    PopupRequest,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser'
import { TokenService } from './token.service'

@Injectable({
    providedIn: 'root',
})
export class AuthTokenService {
    private authService = inject(MsalService)
    private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG)
    private tokenService = inject(TokenService)

    private refreshPromise: Promise<string | null> | null = null
    private readonly tokenRefreshBufferMs = 60_000

    public async getValidToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
        const activeAccount = this.ensureActiveAccount()
        if (!activeAccount) {
            this.tokenService.set(null)
            return null
        }

        const cachedToken = this.tokenService.get()() ?? activeAccount.idToken ?? null
        if (!options?.forceRefresh && cachedToken && !this.isTokenExpiringSoon(cachedToken)) {
            return cachedToken
        }

        return this.refreshToken(options?.forceRefresh ?? false)
    }

    public isTokenExpiringSoon(token: string | null): boolean {
        if (!token) {
            return true
        }

        try {
            const payload = JSON.parse(window.atob(token.split('.')[1] ?? '')) as { exp?: number }
            if (!payload.exp) {
                return true
            }

            return payload.exp * 1000 - Date.now() <= this.tokenRefreshBufferMs
        } catch {
            return true
        }
    }

    private async refreshToken(forceRefresh: boolean): Promise<string | null> {
        if (this.refreshPromise) {
            return this.refreshPromise
        }

        this.refreshPromise = this.acquireFreshToken(forceRefresh).finally(() => {
            this.refreshPromise = null
        })

        return this.refreshPromise
    }

    private async acquireFreshToken(forceRefresh: boolean): Promise<string | null> {
        const activeAccount = this.ensureActiveAccount()
        if (!activeAccount) {
            this.tokenService.set(null)
            return null
        }

        const request = {
            ...(this.msalGuardConfig.authRequest ?? {}),
            account: activeAccount,
            forceRefresh,
        } as SilentRequest

        try {
            const result = await this.authService.instance.acquireTokenSilent(request)
            return this.persistToken(result)
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
                if (this.isInIframe()) {
                    const result = await this.authService.instance.acquireTokenPopup({
                        ...(this.msalGuardConfig.authRequest ?? {}),
                        scopes: ['openid', 'profile', 'User.Read'],
                    } as PopupRequest)
                    return this.persistToken(result)
                }

                this.authService.loginRedirect({
                    ...(this.msalGuardConfig.authRequest ?? {}),
                    scopes: ['openid', 'profile', 'User.Read'],
                } as RedirectRequest)
                return null
            }

            throw error
        }
    }

    private persistToken(result: AuthenticationResult): string | null {
        if (result.account) {
            this.authService.instance.setActiveAccount(result.account)
        }

        const token = result.idToken || this.authService.instance.getActiveAccount()?.idToken || null
        this.tokenService.set(token)

        return token
    }

    private isInIframe(): boolean {
        return window !== window.parent && !window.opener
    }

    private ensureActiveAccount(): AccountInfo | null {
        let activeAccount = this.authService.instance.getActiveAccount()

        if (!activeAccount) {
            const [firstAccount] = this.authService.instance.getAllAccounts()
            if (firstAccount) {
                this.authService.instance.setActiveAccount(firstAccount)
                activeAccount = firstAccount
            }
        }

        return activeAccount
    }
}
